import { ref, computed } from 'vue';
import Request from '@/services/request';
import { SELLER_CATALOG_SEARCH } from "@/utils/enum/endpoints";

const Req = new Request();

const DEFAULT_PAGE_COUNT = -1;
const DEFAULT_RESULT_COUNT = -1;

export default function useService(pagination, searchText, filterData) {
  const data = ref(null);
  const filtersItems = ref(null)
  const totalResultCount = ref(DEFAULT_RESULT_COUNT);
  const error = ref(null);
  const errorMessage = ref(null);
  const isLoading = ref(false);
  const filtersUpdating = ref(true);
  const request = ref(null);
  const pageCount = ref(0);
  const searchValueSuccess = ref(false);
  const searchTextForTemplate = ref(null);
  const didNotFind = ref(null);
  const failedSearchValue = ref(false);

  const fetchSearchData = async (sendFilterParams) => {
    const payload = determinePayload(searchText.value);
    if (!payload) {
      return;
    }
    const requestParams = computed(() => {
      const { pageSize, pageIndex } = pagination.value;
      const currentPage = pageIndex;
      const params = {
        page: currentPage.toString(),
        size: pageSize.toString(),
      };

      if (
        sendFilterParams &&
        (
          filterData.value.filters.brands.length > 0 ||
          filterData.value.filters.categories.length > 0
        )
      ) {
        params.filters = {
          brands: filterData.value.filters.brands.map(i => i.replace(/\s\(\d+\)$/, '')),
          categories: filterData.value.filters.categories.map(i => i.replace(/\s\(\d+\)$/, '')),
        };
      }
      return params;
    });
    isLoading.value = true;
    filtersUpdating.value = !requestParams.value.hasOwnProperty('filters');

    request.value = Req.post(
      SELLER_CATALOG_SEARCH,
      {
        ...payload,
        ...requestParams.value,
      }
    )
      .then(async (response) => {
        if (response.status === 200 && response.data) {
          data.value = response.data.content || [];
          searchValueSuccess.value = true;
          searchTextForTemplate.value = searchText.value;
          error.value = null;
          totalResultCount.value = computed(() => {
            return new Intl.NumberFormat('tr-TR').format(response.data.totalElements);
          }) ?? DEFAULT_PAGE_COUNT;
          didNotFind.value = data.value.length === 0;
          pageCount.value = response.data.totalPages;

          if (filtersUpdating.value && response.data.filters) {
            updateFilterItems(response.data.filters);
          }
        } else {
          throw new Error('Network response was not OK');
        }
      })
      .catch((err) => {
        error.value = err;
        data.value = null;
        totalResultCount.value = DEFAULT_PAGE_COUNT;
        searchValueSuccess.value = false;
        
        if (err?.data?.errors && Array.isArray(err.data.errors) && err.data.errors.length > 0) {
          errorMessage.value = err.data.errors[0].message;
        } else {
          errorMessage.value = null;
        }
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const determinePayload = (searchText) => {
    failedSearchValue.value = false;
    return {
      searchParam: searchText || '',
    };
  };

  function updateFilterItems(filters) {
    const updatedFilterItems = { ...filtersItems.value };

    if (filters.categories) {
      updatedFilterItems.categories = {
        title: 'Kategoriler',
        items: Array.isArray(filters.categories)
          ? filters.categories.map((item, index) => {
              if (typeof item === 'object' && item !== null) {
                return {
                  id: String(index + 1),
                  label: item.count ? `${item.name} (${item.count})` : item.name,
                  name: item.name,
                  count: new Intl.NumberFormat('tr-TR').format(item.count ?? null),
                };
              }
              return {
                id: String(index + 1),
                label: item,
                name: item,
                count: null,
              };
            })
          : [],
      };
    }

    if (filters.brands) {
      updatedFilterItems.brands = {
        title: 'Markalar',
        items: Array.isArray(filters.brands)
          ? filters.brands.map((item, index) => {
              if (typeof item === 'object' && item !== null) {
                return {
                  id: String(index + 1),
                  label: item.count ? `${item.name} (${item.count})` : item.name,
                  name: item.name,
                  count: new Intl.NumberFormat('tr-TR').format(item.count ?? null),
                };
              }
              return {
                id: String(index + 1),
                label: item,
                name: item,
                count: null,
              };
            })
          : [],
      };
    }
    filtersItems.value = updatedFilterItems;
  }

  return {
    data,
    totalResultCount,
    pageCount,
    error,
    errorMessage,
    isLoading,
    determinePayload,
    fetchSearchData,
    searchValueSuccess,
    searchTextForTemplate,
    didNotFind,
    failedSearchValue,
    filtersItems,
    updateFilterItems,
    filtersUpdating
  };
}
